import {
	extractZoneIdFromPresenceCondition,
	validatePresenceConditionData,
	normalizePresenceConditionValue,
	getPresenceConditionType,
} from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/presence-helpers';
import { ILLUMINATION_BANDS } from 'src/components/Scenes/helpers/init_device_conditions';

export interface ExtractedCondition {
	conditionName: string;
	conditionValue: string | number;
	deviceZoneId: string;
	operator: string;
}

export interface PresenceData {
	conditionName: string;
	conditionValue: string | number;
	deviceZoneId: string;
	operator: string;
	originalValue: any;
	conditionType: string;
}

export interface InitializationResult {
	selectedPresenceMode: string;
	selectedZone: string;
	selectedZoneProperty: string;
	conditionValue: string | number;
}

/**
 * Extract presence condition from scene data - main entry point
 */
export function extractPresenceConditionFromScene(sceneData: any): ExtractedCondition | null {
	// Only use sceneData.scene as the source of truth in edit mode
	if (!sceneData.scene) {
		console.warn('No sceneData.scene available for extraction');
		return null;
	}

	const extracted = extractPresenceDataFromScene(sceneData.scene);
	if (extracted) {
		const extractedCondition = {
			conditionName: extracted.conditionName,
			conditionValue: extracted.conditionValue,
			deviceZoneId: extracted.deviceZoneId,
			operator: extracted.operator,
		};

		// Validate the extracted condition
		const isValid = validatePresenceConditionData(
			{
				conditionName: extracted.conditionName,
				deviceZoneId: extracted.deviceZoneId,
				value: extracted.conditionValue,
				operator: extracted.operator,
			},
			'PresenceConditionExtractor.extractPresenceConditionFromScene',
		);

		if (isValid) {
			return extractedCondition;
		} else {
			console.warn('Scene extraction failed validation', extractedCondition);
		}
	}

	return null;
}

/**
 * Returns true if the rr string refers to a discrete lux band condition
 * (`…:lux` but NOT `…:lux_i`).
 */
function isLuxBandRr(rr: string): boolean {
	return /:lux$/.test(rr) && !/:lux_i$/.test(rr);
}

/**
 * Extract presence data directly from scene object
 */
export function extractPresenceDataFromScene(scene: any): PresenceData | null {
	if (!scene) {
		return null;
	}

	let conditionName: string | undefined,
		operator: string | undefined,
		value: any,
		deviceZoneId: string | undefined;

	// Format 1: Direct format with rr, thsop, thsval fields
	if (scene.rr && scene.thsop && scene.thsval !== undefined) {
		const rrMatch = scene.rr.match(/:(prsn_(?:st|nobj_d|nobj)4\d{3})$/);

		if (isLuxBandRr(scene.rr)) {
			// Discrete band condition (new): `dev:XXXX:lux`
			conditionName = 'luxury';
			operator = scene.thsop;
			value = scene.thsval;
			deviceZoneId = '';
		} else if (!rrMatch && scene.rr.includes('lux_i')) {
			// Legacy numeric condition — extract for display; selectedZoneProperty will be left blank
			conditionName = 'lux_i';
			operator = scene.thsop;
			value = scene.thsval;
			deviceZoneId = '';
		} else if (rrMatch) {
			conditionName = rrMatch[1];
			operator = scene.thsop;
			value = scene.thsval;
		} else {
			console.log('DEBUG: No presence condition found in rr field', { rr: scene.rr });
			return null;
		}
	}
	// Format 2: Nested eq array format
	else if (scene.eq && Array.isArray(scene.eq) && scene.eq.length >= 2) {
		const [rrObj, val] = scene.eq;

		if (rrObj && rrObj.rr) {
			const rrMatch = rrObj.rr.match(/:(prsn_(?:st|nobj_d|nobj)4\d{3})$/);

			if (isLuxBandRr(rrObj.rr)) {
				conditionName = 'luxury';
				operator = 'eq';
				value = val;
				deviceZoneId = '';
			} else if (!rrMatch && rrObj.rr.includes('lux_i')) {
				conditionName = 'lux_i';
				operator = 'lt';
				value = val;
				deviceZoneId = '';
			} else if (rrMatch) {
				conditionName = rrMatch[1];
				operator = 'eq';
				value = val;
			} else {
				console.log('DEBUG: No presence condition found in nested eq rr field', {
					rr: rrObj.rr,
				});
				return null;
			}
		} else {
			return null;
		}
	} else {
		console.log('DEBUG: Scene object does not match expected presence condition formats', scene);
		return null;
	}

	// For luminosity conditions, we don't need to extract zone ID
	if (conditionName === 'luxury' || conditionName === 'lux_i') {
		deviceZoneId = '';
	} else {
		deviceZoneId = extractZoneIdFromPresenceCondition(conditionName as string);
		if (!deviceZoneId) {
			console.log('DEBUG: Could not extract zone ID from condition', { conditionName });
			return null;
		}
	}

	// Normalize the condition value based on type
	let conditionType: string;
	let normalizedValue: string | number;

	if (conditionName === 'luxury') {
		conditionType = 'luminosity';
		normalizedValue = String(value); // Keep the band string as-is
	} else if (conditionName === 'lux_i') {
		conditionType = 'luminosity_legacy';
		normalizedValue = Number(value) || 0;
	} else {
		conditionType = getPresenceConditionType(conditionName as string) as string;
		normalizedValue = normalizePresenceConditionValue(
			value,
			conditionType === 'state' ? 'state' : 'object_count',
		);
	}

	const result: PresenceData = {
		conditionName: conditionName as string,
		conditionValue: normalizedValue,
		deviceZoneId: deviceZoneId as string,
		operator: operator as string,
		originalValue: value,
		conditionType,
	};

	if (import.meta.env.DEV) {
		console.log('DEBUG: Extracted presence condition from scene', result);
	}

	return result;
}

/**
 * Initialize presence device form state from extracted scene data
 */
export function initializePresenceDeviceFromScene(sceneData: any): InitializationResult | null {
	const extractedCondition = extractPresenceConditionFromScene(sceneData);

	if (extractedCondition) {
		const { conditionName, conditionValue, deviceZoneId, operator } = extractedCondition;

		let selectedPresenceMode = 'zone_detection';
		let selectedZone = '';
		let selectedZoneProperty = '';

		if (deviceZoneId) {
			selectedZone = `zone_${deviceZoneId}`;
		}

		if (conditionName === 'luxury') {
			// Discrete band luminosity condition — restore the selected band
			selectedPresenceMode = 'luminosity';
			selectedZoneProperty = String(conditionValue); // 'dark' | 'twilight' | 'bright'
		} else if (conditionName === 'lux_i') {
			selectedPresenceMode = 'illuminance_threshold';
			const op = String(operator || 'lt').toLowerCase();
			selectedZoneProperty = op === 'gt' || op === 'mt' ? 'gt' : 'lt';
		} else if (conditionName && conditionName.startsWith('prsn_st4')) {
			if (operator === 'eq') {
				if (String(conditionValue) === 'true') {
					selectedZoneProperty = 'presence';
				} else if (String(conditionValue) === 'false') {
					selectedZoneProperty = 'absence';
				} else {
					console.warn('Invalid state value for presence condition:', conditionValue);
					selectedZoneProperty = 'presence';
				}
			} else {
				console.warn('Non-eq operator used for state condition:', operator);
				selectedZoneProperty = 'presence';
			}
		} else if (conditionName && conditionName.startsWith('prsn_nobj_d4')) {
			if (operator === 'gt') {
				selectedZoneProperty = 'enter';
			} else if (operator === 'lt') {
				selectedZoneProperty = 'leave';
			} else {
				selectedZoneProperty = 'enter';
			}
		} else if (conditionName && conditionName.startsWith('prsn_nobj4')) {
			selectedZoneProperty = 'object_count';
		} else {
			selectedZoneProperty = 'presence';
		}

		return {
			selectedPresenceMode,
			selectedZone,
			selectedZoneProperty,
			conditionValue:
				conditionName === 'lux_i' ? String(Number(conditionValue) || 0) : conditionValue,
		};
	}

	return null;
}
